import {
  ONBOARDING_STEP_COPY,
  type OnboardingStepNumber,
  type OnboardingStepState,
  type OnboardingStepStatus,
} from '@doneverse/shared'
import { Link } from '@tanstack/react-router'
import {
  BookOpen,
  Brain,
  CalendarCheck,
  Check,
  Compass,
  FileSignature,
  GraduationCap,
  Lock,
  PhoneCall,
  Rocket,
  Trophy,
  Unlock,
  Users,
  type LucideIcon,
} from 'lucide-react'
import styles from './OnboardingStepRail.module.css'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useOnboardingLock } from '@/hooks/useOnboardingLock'
import { stepRailDestination } from '@/lib/onboardingLock'
import { scrollToHash } from '@/lib/scroll'

/**
 * A face for each step, used by the rail's markers and by the panel's badge.
 *
 * In the dashboard rather than beside `ONBOARDING_STEP_COPY` in
 * `@doneverse/shared`, which has no React dependency and is imported by the API.
 * Keyed by the shared step union so a new step fails to compile here rather than
 * rendering a blank circle.
 */
export const ONBOARDING_STEP_ICONS: Record<OnboardingStepNumber, LucideIcon> = {
  1: FileSignature,
  2: Brain,
  3: CalendarCheck,
  4: Compass,
  5: Unlock,
  6: GraduationCap,
  7: BookOpen,
  8: PhoneCall,
  9: Users,
  10: Rocket,
  11: Trophy,
}

const STATUS_WORD: Record<OnboardingStepStatus, string> = {
  complete: 'complete',
  active: 'ready now',
  locked: 'locked',
}

/**
 * The ten-node journey, and the way back into it.
 *
 * Every node responds, not just the active ones — that is what replaced the old
 * expandable journey popover, and it has to be every node rather than the active
 * set for two reasons: after the unlock 6/7/8/9 are open AT ONCE and only one
 * can be described below, and a founder looking back at a step they have already
 * finished is exactly what "see your journey" meant.
 *
 * What a node DOES depends on whether its step is somewhere to go, which is
 * {@link stepRailDestination}'s question:
 *
 *   it is → a `<Link>` to it. "Take me back to Foundations" wants the videos,
 *     not a one-line description of them, and the tracker is drawn by
 *     `PageLayout` on every page, so arriving keeps the journey on screen above
 *     the thing the founder came for. Real anchors, so middle-click and
 *     open-in-new-tab work;
 *   it is not → a `<button>` that only re-points the panel below. Steps 1 and 4
 *     (in-shell surfaces), the steps still shut, and — before the unlock —
 *     every step but the active one, which the router would bounce.
 *
 * `onSelect` is for the browse-only nodes. A node that navigates does not need
 * it — the panel reads the step it is describing off the route
 * (`onboardingStepForPath`), so the card follows the founder on the FIRST press
 * rather than waiting for a second one to survive the remount. It is passed
 * anyway, for the founder pressing the node of the page they already stand on,
 * where there is no navigation to read.
 */
export function OnboardingStepRail({
  steps,
  selected,
  onSelect,
  controls,
}: {
  steps: OnboardingStepState[]
  selected: OnboardingStepNumber
  onSelect: (step: OnboardingStepNumber) => void
  /** Id of the panel body these nodes drive. */
  controls: string
}) {
  const lock = useOnboardingLock()
  const { hasFlag } = useFeatureFlags()

  return (
    <ol
      className={styles.rail}
      style={{ '--rail-steps': steps.length } as React.CSSProperties}
      aria-label="Onboarding steps"
    >
      {steps.map((step, index) => {
        const copy = ONBOARDING_STEP_COPY[step.stepNumber]
        const isSelected = step.stepNumber === selected
        const destination = stepRailDestination(step, lock, hasFlag)
        // A node draws its own icon while it is the one being read about, and
        // its status glyph otherwise — which is what keeps a finished step
        // recognisable as itself when the founder goes back to look at it.
        const Glyph =
          isSelected || step.status === 'active'
            ? ONBOARDING_STEP_ICONS[step.stepNumber]
            : step.status === 'complete'
              ? Check
              : Lock

        const face = (
          <>
            <span className={styles.marker}>
              <Glyph aria-hidden />
            </span>
            {/* The visible label is short enough to fit ten to a row; the
                accessible name is the whole sentence. */}
            <span className={styles.label} aria-hidden>
              {index + 1}. {copy.shortTitle}
            </span>
            <span className="sr-only">
              Step {index + 1} of {steps.length}: {copy.title} — {STATUS_WORD[step.status]}
            </span>
          </>
        )

        return (
          <li
            key={step.stepNumber}
            className={styles.node}
            data-status={step.status}
            data-selected={isSelected ? 'true' : undefined}
            // The segment LEADING INTO this node, so the line fills as the
            // journey does rather than running ahead of it.
            data-prev-complete={index > 0 && steps[index - 1].status === 'complete' ? 'true' : undefined}
          >
            {destination ? (
              // No `aria-controls`: this one leaves the page rather than
              // re-pointing the panel below, and a link that claimed to drive an
              // element it navigates away from would be a lie to a screen
              // reader.
              <Link
                to={destination.to}
                hash={destination.hash}
                className={styles.button}
                // `scrollToHash` is what makes the node of the page the founder
                // is already on do something — see `lib/scroll`.
                onClick={() => {
                  onSelect(step.stepNumber)
                  if (destination.hash) scrollToHash(destination.hash)
                }}
                aria-current={isSelected ? 'step' : undefined}
              >
                {face}
              </Link>
            ) : (
              <button
                type="button"
                className={styles.button}
                onClick={() => onSelect(step.stepNumber)}
                aria-current={isSelected ? 'step' : undefined}
                aria-controls={controls}
              >
                {face}
              </button>
            )}
          </li>
        )
      })}
    </ol>
  )
}
